/**
 * AccessibilityHero — Weidner Via6 page hero for /accessibility.
 * See `FloorPlansHero.tsx` and `_PageImageHeroBody.tsx` for the shared
 * pattern. Owns its slot registration, contentSchema, and hidden
 * literal-path anchors; visual rendering is delegated to the body.
 */

import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { PageImageHeroBody } from './_PageImageHeroBody';

const bgDef = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Accessibility hero image',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

export function AccessibilityHero() {
  return (
    <>
      <PageImageHeroBody
        titlePath="accessibilityhero.title"
        bgPath="accessibilityhero.bg_image"
        bgDef={bgDef}
        defaultTitle="Accessibility"
        keywords={[]}
        pageHint="accessibilityhero"
        altText="Accessibility hero image"
      />

      <RichTextContent
        path="accessibilityhero.title"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="accessibilityhero.bg_image"
        definition={bgDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AccessibilityHero',
  displayName: 'Accessibility Hero',
  tokens: {
    'accessibilityhero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: 'Accessibility',
    },
  },
  slotGroups: {
    'accessibilityhero.bg_image': {
      label: 'Background Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: 'property_featured_image',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'Accessibility hero image',
        },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
  },
};

(AccessibilityHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AccessibilityHero;
